// V2 Member Wallet Pages — Crypto, Global Send, Vault
// Powered by GET /api/v2/member/me + POST /api/v2/route (mock router responses)

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useBranding } from "../BrandingContext";
import {
  ChevronRight, ChevronDown, Loader2, Coins, Globe, Vault as VaultIcon,
  Shield, Info, ArrowUpRight, ArrowDownLeft, ArrowRight, Plane, MapPin,
  TrendingUp, Building2, CheckCircle2, AlertCircle, Lock, Zap, DollarSign,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─────────── Shared sub-components ───────────

const PageHeader = ({ title, subtitle, icon: Icon }) => {
  const navigate = useNavigate();
  return (
    <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
      <div className="container mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/dashboard")}
          className="p-2 -ml-2 rounded-lg hover:bg-slate-100 transition"
          data-testid="page-header-back"
        >
          <ChevronRight className="w-5 h-5 rotate-180 text-slate-600" />
        </button>
        {Icon && (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#007377] to-[#005a5d] flex items-center justify-center shadow-sm">
            <Icon className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-lg md:text-xl font-bold text-slate-900 truncate">{title}</h1>
          {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const DisclosureBanner = ({ tone = "warn", icon: Icon = AlertCircle, children, testId }) => {
  const tones = {
    warn: "bg-amber-50 border-amber-200 text-amber-900",
    info: "bg-sky-50 border-sky-200 text-sky-900",
    success: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div
      className={`rounded-xl border ${tones[tone]} px-4 py-3 flex items-start gap-3 text-sm leading-snug`}
      data-testid={testId}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
};

const Footer = () => {
  const brand = useBranding();
  return (
    <footer className="border-t border-slate-200 bg-white mt-8 py-4 px-4 text-center">
      <p className="text-xs text-slate-400">{brand.walletName} is a service of {brand.name}. {brand.insuranceLabel}.</p>
    </footer>
  );
};

// ─────────── Hook: load v2 member ───────────

const useV2Member = () => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/v2/member/me`);
      setMember(r.data);
      return r.data;
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/v2/member/me`)
      .then((r) => { if (!cancelled) setMember(r.data); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { member, loading, error, reload };
};

// ─────────── Shared mini-components ───────────

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2600);
  }, []);
  const node = toast ? (
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 ${toast.kind === "error" ? "bg-rose-600" : "bg-emerald-600"} text-white px-4 py-2.5 rounded-lg shadow-lg z-50 text-sm font-medium`} data-testid="walletv2-toast">
      {toast.msg}
    </div>
  ) : null;
  return [show, node];
};

const AmountModal = ({ title, subtitle, submitLabel, onSubmit, onClose, max, testIdPrefix = "modal" }) => {
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const amt = parseFloat(amount) || 0;
  const handleSubmit = async () => {
    if (amt <= 0) { setErr("Enter an amount"); return; }
    if (max != null && amt > max) { setErr(`Max available: $${max.toFixed(2)}`); return; }
    setSubmitting(true);
    try {
      await onSubmit(amt);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-amount-modal`}>
        <div>
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200 px-4 py-3">
          <span className="text-3xl font-bold text-slate-400">$</span>
          <input type="number" autoFocus value={amount} placeholder="0.00"
            onChange={(e) => { setAmount(e.target.value); setErr(""); }}
            className="flex-1 bg-transparent outline-none text-3xl font-bold"
            data-testid={`${testIdPrefix}-amount-input`} />
        </div>
        {max != null && <p className="text-xs text-slate-500">Available: ${max.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>}
        {err && <p className="text-xs text-rose-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:border-slate-300 transition">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || amt <= 0}
            className="flex-1 py-3 rounded-xl bg-[#007377] text-white font-bold hover:bg-[#005a5d] disabled:opacity-50 transition"
            data-testid={`${testIdPrefix}-amount-submit`}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================
// 1) CRYPTO PAGE — /wallet/crypto
// =============================================================

const CHAINS = [
  { id: "ethereum", name: "Ethereum", short: "ETH", color: "bg-indigo-100 text-indigo-700" },
  { id: "base",     name: "Base",     short: "BASE", color: "bg-blue-100 text-blue-700" },
  { id: "solana",   name: "Solana",   short: "SOL",  color: "bg-purple-100 text-purple-700" },
  { id: "polygon",  name: "Polygon",  short: "POLY", color: "bg-violet-100 text-violet-700" },
];

// Seed holdings shown ONLY if wallet is still empty (new members)
const SEED_HOLDINGS = [
  { chain: "base",     asset: "USDC", balance: 250.0 },
  { chain: "ethereum", asset: "USDT", balance: 100.0 },
];

export const CryptoPage = () => {
  const { member, loading, reload } = useV2Member();
  const { user: authUser } = useAuth();
  const brand = useBranding();
  const [section, setSection] = useState("holdings");
  const [tradeModal, setTradeModal] = useState(null); // { side: "buy"|"sell", asset, chain }
  const [toast, toastNode] = useToast();

  const stablecoinWallet = member?.wallets?.find((w) => w.type === "STABLECOIN");
  const addresses = stablecoinWallet?.addresses || {};
  const realBalances = stablecoinWallet?.balances || {};
  const liveTotal = Object.values(realBalances).reduce((a, b) => a + (b || 0), 0);
  // Show seed holdings only when the member has zero stablecoin balance
  const hasLive = liveTotal > 0;
  const holdings = hasLive
    ? Object.entries(realBalances).filter(([, v]) => v > 0).map(([asset, balance]) => ({ chain: "base", asset, balance }))
    : SEED_HOLDINGS;
  const totalUSD = hasLive ? liveTotal : SEED_HOLDINGS.reduce((acc, h) => acc + h.balance, 0);

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <PageHeader title="Crypto" subtitle="Stablecoin holdings & transfers" icon={Coins} />

      <main className="container mx-auto px-4 md:px-8 py-6 max-w-3xl">
        {/* Total balance card */}
        <div className="rounded-2xl p-6 mb-5 text-white shadow-lg"
             style={{ background: "linear-gradient(135deg, #0b1d2a 0%, #007377 100%)" }}
             data-testid="crypto-total-card">
          <p className="text-white/60 text-sm mb-1">Total Stablecoin Balance</p>
          <p className="text-4xl font-bold tracking-tight" data-testid="crypto-total-usd">
            ${totalUSD.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </p>
          <p className="text-white/50 text-xs mt-2">USD-equivalent · 4 chains supported</p>
        </div>

        {/* NCUA disclosure */}
        <div className="mb-5">
          <DisclosureBanner tone="warn" icon={Shield} testId="crypto-ncua-disclosure">
            <strong>Stablecoin balances are not {brand.insurance}-insured.</strong> For {brand.insurance}-insured
            digital balances, see your{" "}
            <a href="/wallet/vault" className="underline font-semibold">Vault</a>.
          </DisclosureBanner>
        </div>

        {/* Section tabs */}
        <div className="bg-white rounded-xl p-1 flex gap-1 mb-5 border border-slate-200" data-testid="crypto-tabs">
          {[
            { id: "holdings", label: "Holdings" },
            { id: "buy_sell", label: "Buy / Sell" },
            { id: "send_receive", label: "Send / Receive" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setSection(t.id)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition ${
                section === t.id
                  ? "bg-[#007377] text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`crypto-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {section === "holdings" && (
              <div className="space-y-3" data-testid="crypto-holdings-section">
                {CHAINS.map((chain) => {
                  const onChain = holdings.filter((h) => h.chain === chain.id);
                  return (
                    <div key={chain.id} className="bg-white rounded-xl p-4 border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${chain.color}`}>
                            {chain.short}
                          </span>
                          <span className="font-semibold text-slate-900">{chain.name}</span>
                        </div>
                        <span className="text-xs text-slate-400 font-mono truncate max-w-[160px]">
                          {addresses[chain.id]?.slice(0, 10)}…{addresses[chain.id]?.slice(-4)}
                        </span>
                      </div>
                      {onChain.length === 0 ? (
                        <p className="text-sm text-slate-400 py-2">No balance on this chain</p>
                      ) : (
                        <div className="space-y-2">
                          {onChain.map((h) => (
                            <div
                              key={`${chain.id}-${h.asset}`}
                              className="flex items-center justify-between py-2 border-t border-slate-100"
                              data-testid={`crypto-holding-${chain.id}-${h.asset}`}
                            >
                              <span className="font-medium text-slate-700">{h.asset}</span>
                              <span className="font-bold text-slate-900">
                                {h.balance.toFixed(2)}{" "}
                                <span className="text-slate-400 text-sm font-normal">
                                  · ${h.balance.toFixed(2)}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {section === "buy_sell" && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 text-center" data-testid="crypto-buysell-section">
                <Coins className="w-10 h-10 mx-auto text-[#007377] mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1">Buy & Sell Stablecoins</h3>
                <p className="text-sm text-slate-500 mb-5">
                  Convert USD ↔ USDC, USDT, or PYUSD. Dollar-pegged 1:1.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setTradeModal({ side: "buy", asset: "USDC", chain: "base" })}
                    className="py-3 rounded-xl bg-[#007377] text-white font-semibold hover:bg-[#005a5d] transition"
                    data-testid="crypto-buy-btn">
                    Buy
                  </button>
                  <button
                    onClick={() => setTradeModal({ side: "sell", asset: "USDC", chain: "base" })}
                    className="py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:border-slate-300 transition"
                    data-testid="crypto-sell-btn">
                    Sell
                  </button>
                </div>
              </div>
            )}

            {section === "send_receive" && (
              <div className="space-y-3" data-testid="crypto-sendreceive-section">
                <div className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <ArrowUpRight className="w-5 h-5 text-rose-600" />
                    <h3 className="font-bold text-slate-900">Send Stablecoin</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Send USDC/USDT/PYUSD to any wallet address on Ethereum, Base, Solana, or Polygon.
                  </p>
                  <button disabled className="w-full py-3 rounded-xl bg-slate-200 text-slate-500 font-semibold cursor-not-allowed" data-testid="crypto-send-btn">
                    Coming soon — real chain integration (Prompt 6+)
                  </button>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-bold text-slate-900">Receive Stablecoin</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    Share one of your wallet addresses to receive USDC/USDT/PYUSD.
                  </p>
                  <div className="space-y-2">
                    {CHAINS.map((c) => (
                      <div key={c.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${c.color}`}>{c.short}</span>
                        <span className="font-mono text-xs text-slate-600 truncate max-w-[60%]">
                          {addresses[c.id] || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {tradeModal && (
        <CryptoTradeModal
          side={tradeModal.side}
          initialAsset={tradeModal.asset}
          initialChain={tradeModal.chain}
          available={realBalances}
          walletBalance={member?.wallet_balance ?? authUser?.wallet_balance ?? 0}
          onClose={() => setTradeModal(null)}
          onDone={async (msg) => { toast(msg); await reload(); setTradeModal(null); }}
        />
      )}

      {toastNode}
      <Footer />
    </div>
  );
};

const CryptoTradeModal = ({ side, initialAsset, initialChain, available, walletBalance, onClose, onDone }) => {
  const [asset, setAsset] = useState(initialAsset);
  const [chain, setChain] = useState(initialChain);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const amt = parseFloat(amount) || 0;
  const max = side === "buy" ? walletBalance : (available[asset] || 0);

  const submit = async () => {
    if (amt <= 0) { setErr("Enter an amount"); return; }
    if (amt > max) { setErr(`Max: $${max.toFixed(2)}`); return; }
    setSubmitting(true);
    try {
      const url = side === "buy" ? `${API}/v2/crypto/buy` : `${API}/v2/crypto/sell`;
      await axios.post(url, { amount_usd: amt, asset, chain });
      const verb = side === "buy" ? "Bought" : "Sold";
      onDone(`${verb} $${amt.toFixed(2)} ${asset} on ${chain.charAt(0).toUpperCase() + chain.slice(1)}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="crypto-trade-modal">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{side === "buy" ? "Buy stablecoin" : "Sell stablecoin"}</h3>
          <p className="text-xs text-slate-500 mt-1">1:1 USD peg · instant execution</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Asset</label>
            <select value={asset} onChange={(e) => setAsset(e.target.value)} data-testid="crypto-trade-asset"
              className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="USDC">USDC</option>
              <option value="USDT">USDT</option>
              <option value="PYUSD">PYUSD</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Chain</label>
            <select value={chain} onChange={(e) => setChain(e.target.value)} data-testid="crypto-trade-chain"
              className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="ethereum">Ethereum</option>
              <option value="base">Base</option>
              <option value="solana">Solana</option>
              <option value="polygon">Polygon</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200 px-4 py-3">
          <span className="text-3xl font-bold text-slate-400">$</span>
          <input type="number" autoFocus value={amount} placeholder="0.00"
            onChange={(e) => { setAmount(e.target.value); setErr(""); }}
            className="flex-1 bg-transparent outline-none text-3xl font-bold"
            data-testid="crypto-trade-amount-input" />
        </div>
        <p className="text-xs text-slate-500">Available: ${max.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
        {err && <p className="text-xs text-rose-600">{err}</p>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="crypto-trade-submit"
            className="flex-1 py-3 rounded-xl bg-[#007377] text-white font-bold hover:bg-[#005a5d] disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `${side === "buy" ? "Buy" : "Sell"} ${asset}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const CORRIDORS = [
  { code: "MX", name: "Mexico",      flag: "🇲🇽", currency: "MXN", rate: 17.10 },
  { code: "PH", name: "Philippines", flag: "🇵🇭", currency: "PHP", rate: 56.20 },
  { code: "IN", name: "India",       flag: "🇮🇳", currency: "INR", rate: 83.40 },
  { code: "CN", name: "China",       flag: "🇨🇳", currency: "CNY", rate: 7.21 },
  { code: "NG", name: "Nigeria",     flag: "🇳🇬", currency: "NGN", rate: 1480.0 },
  { code: "KE", name: "Kenya",       flag: "🇰🇪", currency: "KES", rate: 129.5 },
  { code: "BR", name: "Brazil",      flag: "🇧🇷", currency: "BRL", rate: 4.95 },
  { code: "CO", name: "Colombia",    flag: "🇨🇴", currency: "COP", rate: 4050.0 },
  { code: "GT", name: "Guatemala",   flag: "🇬🇹", currency: "GTQ", rate: 7.78 },
  { code: "SV", name: "El Salvador", flag: "🇸🇻", currency: "USD", rate: 1.0 },
  { code: "HN", name: "Honduras",    flag: "🇭🇳", currency: "HNL", rate: 24.7 },
  { code: "DO", name: "Dom. Republic", flag: "🇩🇴", currency: "DOP", rate: 58.5 },
  { code: "PE", name: "Peru",        flag: "🇵🇪", currency: "PEN", rate: 3.72 },
  { code: "EC", name: "Ecuador",     flag: "🇪🇨", currency: "USD", rate: 1.0 },
  { code: "VN", name: "Vietnam",     flag: "🇻🇳", currency: "VND", rate: 24800.0 },
  { code: "BD", name: "Bangladesh",  flag: "🇧🇩", currency: "BDT", rate: 119.0 },
  { code: "PK", name: "Pakistan",    flag: "🇵🇰", currency: "PKR", rate: 278.0 },
  { code: "EG", name: "Egypt",       flag: "🇪🇬", currency: "EGP", rate: 49.2 },
  { code: "ET", name: "Ethiopia",    flag: "🇪🇹", currency: "ETB", rate: 124.0 },
  { code: "GH", name: "Ghana",       flag: "🇬🇭", currency: "GHS", rate: 14.8 },
];

const DELIVERY_METHODS = [
  { id: "bank_account",      label: "Bank Account",      icon: Building2, eta: "Same day" },
  { id: "mobile_wallet",     label: "Mobile Wallet",     icon: ChevronRight, eta: "1 hour" },
  { id: "cash_pickup",       label: "Cash Pickup",       icon: MapPin, eta: "Instant" },
  { id: "stablecoin_wallet", label: "Stablecoin Wallet", icon: Coins, eta: "Instant" },
];

const SPEEDS = [
  { id: "instant",  label: "Instant",  fee: 4.99, etaText: "Within minutes" },
  { id: "1hour",    label: "1 hour",   fee: 2.99, etaText: "Within 60 min" },
  { id: "same_day", label: "Same day", fee: 0.99, etaText: "By end of day" },
];

const FX_SPREAD = 0.015; // 1.5% built-in spread (transparent)

export const GlobalSendPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState(CORRIDORS[0]);
  const [amount, setAmount] = useState("500");
  const [delivery, setDelivery] = useState(DELIVERY_METHODS[0].id);
  const [speed, setSpeed] = useState("instant");
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null);

  const amtUSD = parseFloat(amount) || 0;
  const speedCfg = SPEEDS.find((s) => s.id === speed);
  const effectiveRate = country.rate * (1 - FX_SPREAD);
  const recipientAmount = amtUSD * effectiveRate;
  const spreadCost = amtUSD * FX_SPREAD;

  const handleConfirm = async () => {
    setSubmitting(true);
    setDecision(null);
    try {
      const mockIban = "DE89370400440532013000";
      const recipient_identifier = delivery === "stablecoin_wallet"
        ? "0x" + Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join("")
        : mockIban;
      const res = await axios.post(`${API}/v2/send/global`, {
        recipient_identifier,
        amount: amtUSD,
        source_currency: "USD",
        target_currency: country.currency,
        member_preference: speed === "same_day" ? "cost_priority" : (speed === "instant" ? "speed_priority" : "default"),
        memo: `Global send to ${country.name} via ${delivery}`,
      });
      setDecision(res.data);
      setStep(3);
      // Auto-refresh dashboard balances after successful send
      await refreshUser();
    } catch (e) {
      setDecision({ error: e.response?.data?.detail || "Send failed" });
      setStep(3);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <PageHeader title="Global Send" subtitle="Send money to 20+ countries" icon={Globe} />

      <main className="container mx-auto px-4 md:px-8 py-6 max-w-2xl">
        {step === 1 && (
          <div className="space-y-5" data-testid="globalsend-step-1">
            {/* Country picker */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Send to
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-3" data-testid="globalsend-corridors">
                {CORRIDORS.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCountry(c)}
                    className={`flex flex-col items-center justify-center py-3 px-1 rounded-lg border-2 transition ${
                      country.code === c.code
                        ? "border-[#007377] bg-teal-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`globalsend-corridor-${c.code}`}
                  >
                    <span className="text-2xl leading-none">{c.flag}</span>
                    <span className="text-[10px] font-semibold text-slate-700 mt-1">{c.code}</span>
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-600 mt-3">
                Selected: <strong>{country.name}</strong> · Pays in {country.currency}
              </p>
            </div>

            {/* Amount */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                You send
              </label>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-3xl font-bold text-slate-400">$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 text-3xl font-bold outline-none bg-transparent"
                  data-testid="globalsend-amount-input"
                />
                <span className="text-sm font-semibold text-slate-500">USD</span>
              </div>

              {/* FX preview */}
              <div className="mt-4 p-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl text-white" data-testid="globalsend-fx-preview">
                <p className="text-white/60 text-xs uppercase tracking-wide font-semibold">Recipient gets</p>
                <p className="text-2xl font-bold mt-1">
                  {recipientAmount.toLocaleString("en-US", { maximumFractionDigits: 2 })}{" "}
                  <span className="text-white/60 text-base">{country.currency}</span>
                </p>
                <div className="flex items-center justify-between mt-3 text-xs text-white/70">
                  <span>Rate: 1 USD = {effectiveRate.toFixed(2)} {country.currency}</span>
                  <span>Spread: ${spreadCost.toFixed(2)} ({(FX_SPREAD * 100).toFixed(1)}%)</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={amtUSD <= 0}
              className="w-full py-4 rounded-xl bg-[#007377] text-white font-bold hover:bg-[#005a5d] transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="globalsend-continue-btn"
            >
              Continue <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="globalsend-step-2">
            {/* Delivery method */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Delivery method
              </label>
              <div className="grid grid-cols-2 gap-2 mt-3" data-testid="globalsend-delivery-methods">
                {DELIVERY_METHODS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setDelivery(m.id)}
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition ${
                      delivery === m.id
                        ? "border-[#007377] bg-teal-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`globalsend-delivery-${m.id}`}
                  >
                    <m.icon className="w-5 h-5 text-[#007377] mt-0.5" />
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{m.label}</p>
                      <p className="text-xs text-slate-500">{m.eta}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Speed slider */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Speed vs cost
              </label>
              <div className="space-y-2 mt-3" data-testid="globalsend-speed-options">
                {SPEEDS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSpeed(s.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition ${
                      speed === s.id
                        ? "border-[#007377] bg-teal-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`globalsend-speed-${s.id}`}
                  >
                    <div className="text-left">
                      <p className="font-semibold text-slate-900">{s.label}</p>
                      <p className="text-xs text-slate-500">{s.etaText}</p>
                    </div>
                    <p className="font-bold text-slate-900">${s.fee.toFixed(2)} fee</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-900 rounded-2xl p-5 text-white">
              <p className="text-white/60 text-xs uppercase tracking-wide font-semibold mb-3">Order summary</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-white/70">Amount</span><span>${amtUSD.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-white/70">FX spread</span><span>${spreadCost.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-white/70">Speed fee</span><span>${speedCfg.fee.toFixed(2)}</span></div>
                <div className="border-t border-white/10 pt-2 mt-2 flex justify-between font-bold text-base">
                  <span>Total cost</span><span>${(amtUSD + spreadCost + speedCfg.fee).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-emerald-300">
                  <span>Recipient gets</span>
                  <span className="font-bold">{recipientAmount.toFixed(2)} {country.currency}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-4 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:border-slate-300 transition"
                data-testid="globalsend-back-btn"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="flex-[2] py-4 rounded-xl bg-[#007377] text-white font-bold hover:bg-[#005a5d] transition disabled:opacity-50"
                data-testid="globalsend-confirm-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin inline" /> : "Confirm & send"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && decision && (
          <div className="space-y-5" data-testid="globalsend-step-3">
            {decision.error ? (
              <div className="bg-white rounded-2xl p-8 border border-red-200 text-center" data-testid="globalsend-error-card">
                <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  {/^insufficient/i.test(decision.error) ? "Insufficient balance" : "Send failed"}
                </h3>
                <p className="text-sm text-red-600">{decision.error}</p>
                <button
                  onClick={() => setStep(2)}
                  className="mt-5 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold"
                  data-testid="globalsend-retry-btn"
                >
                  Try again
                </button>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white text-center" data-testid="globalsend-success-card">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2" />
                  <h2 className="text-2xl font-bold">Transfer initiated</h2>
                  <p className="text-white/80 text-sm mt-1">
                    {recipientAmount.toFixed(2)} {country.currency} on the way to {country.name}
                  </p>
                </div>

                {/* Smart Router rationale */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="globalsend-router-rationale">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-[#007377]" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Smart Router decision
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1.5 rounded-full bg-[#007377] text-white text-xs font-bold uppercase tracking-wide" data-testid="globalsend-rail-badge">
                      Rail: {decision.chosen_rail || "—"}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">
                      ID: {(decision.decision_id || "").slice(0, 8)}…
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {decision.rationale || "No rationale returned."}
                  </p>
                  {decision.reference_number && (
                    <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                      <p>Reference: <span className="font-mono">{decision.reference_number}</span></p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => { setStep(1); setDecision(null); }}
                    className="py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold hover:border-slate-300 transition"
                    data-testid="globalsend-new-btn"
                  >
                    Send another
                  </button>
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition"
                    data-testid="globalsend-done-btn"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

// =============================================================
// 3) VAULT PAGE — /wallet/vault
// =============================================================
// Member-facing language: "savings", "deposit", "yield". Never "blockchain", "on-chain", or "token".

const APY = 0.0425; // 4.25% — hardcoded for MVP

const generateYieldLedger = (principal) => {
  const days = [];
  const dailyRate = APY / 365;
  const today = new Date();
  let bal = principal;
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const accrual = bal * dailyRate;
    bal += accrual;
    days.push({
      date: d.toISOString().slice(0, 10),
      accrual: Number(accrual.toFixed(4)),
      balance: Number(bal.toFixed(2)),
    });
  }
  return days;
};

export const VaultPage = () => {
  const { member, loading, reload } = useV2Member();
  const { refreshUser, user: authUser } = useAuth();
  const brand = useBranding();
  const [showLedger, setShowLedger] = useState(false);
  const [modal, setModal] = useState(null); // "deposit" | "withdraw" | "pay"
  const [toast, toastNode] = useToast();

  // Read tokenized deposit balance from live v2 data; fall back to demo seed
  const tokenizedWallet = member?.wallets?.find((w) => w.type === "TOKENIZED_DEPOSIT");
  const realPrincipal = tokenizedWallet?.balance || 0;
  const principal = realPrincipal > 0 ? realPrincipal : 1000.0;
  const yieldAccrued = tokenizedWallet?.yield_accrued || 0.0;
  // wallet_balance: prefer v2 fetch (fresh), fall back to AuthContext user
  // (available immediately at login) so the deposit modal never shows $0 due
  // to the v2-fetch race window.
  const walletBalance = member?.wallet_balance ?? authUser?.wallet_balance ?? 0;

  const ledger = useMemo(() => generateYieldLedger(principal), [principal]);
  const totalYield30d = ledger.reduce((acc, d) => acc + d.accrual, 0);

  const refresh = useCallback(async () => {
    await reload();
    await refreshUser();
  }, [reload, refreshUser]);

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <PageHeader title="Vault" subtitle="Insured high-yield savings" icon={VaultIcon} />

      <main className="container mx-auto px-4 md:px-8 py-6 max-w-3xl">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Vault balance card */}
            <div
              className="rounded-2xl p-6 mb-5 text-white shadow-lg relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, #1a3a52 0%, #007377 100%)" }}
              data-testid="vault-balance-card"
            >
              <div className="absolute top-3 right-3 flex items-center gap-2">
                <span className="px-2.5 py-1 bg-emerald-500/20 border border-emerald-300/40 rounded-full text-emerald-100 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" data-testid="vault-insurance-badge">
                  <Shield className="w-3 h-3" /> {brand.insuranceLabel}
                </span>
              </div>
              <p className="text-white/60 text-sm mb-1">Vault Balance</p>
              <p className="text-4xl font-bold tracking-tight" data-testid="vault-balance">
                ${principal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </p>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
                <div>
                  <p className="text-white/60 text-xs">Earning APY</p>
                  <p className="text-2xl font-bold text-emerald-300" data-testid="vault-apy">
                    {(APY * 100).toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-white/60 text-xs">Yield (30d est.)</p>
                  <p className="text-lg font-bold">+${totalYield30d.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-white/60 text-xs">Lifetime yield</p>
                  <p className="text-lg font-bold">+${yieldAccrued.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Insurance disclosure */}
            <div className="mb-5">
              <DisclosureBanner tone="success" icon={Shield} testId="vault-insurance-disclosure">
                Your Vault balance is held as a deposit at {brand.name} and is insured up to{" "}
                <strong>$250,000</strong> per depositor by the <strong>{brand.insurance}</strong>. Yield is paid daily.
              </DisclosureBanner>
            </div>

            {/* Three actions */}
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="vault-actions">
              <button
                onClick={() => setModal("deposit")}
                className="bg-white rounded-2xl p-4 border border-slate-200 hover:border-[#007377] hover:shadow-md transition group text-left"
                data-testid="vault-deposit-btn"
              >
                <div className="w-10 h-10 mb-2 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition">
                  <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-sm font-semibold text-slate-900">Deposit</p>
                <p className="text-xs text-slate-500">Move funds in</p>
              </button>
              <button
                onClick={() => setModal("withdraw")}
                disabled={realPrincipal <= 0}
                className="bg-white rounded-2xl p-4 border border-slate-200 hover:border-[#007377] hover:shadow-md transition group text-left disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="vault-withdraw-btn"
              >
                <div className="w-10 h-10 mb-2 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition">
                  <ArrowUpRight className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-sm font-semibold text-slate-900">Withdraw</p>
                <p className="text-xs text-slate-500">Move to checking</p>
              </button>
              <button
                onClick={() => setModal("pay")}
                disabled={realPrincipal <= 0}
                className="bg-white rounded-2xl p-4 border border-slate-200 hover:border-[#007377] hover:shadow-md transition group text-left disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="vault-pay-btn"
              >
                <div className="w-10 h-10 mb-2 rounded-xl bg-sky-50 flex items-center justify-center group-hover:bg-sky-100 transition">
                  <DollarSign className="w-5 h-5 text-sky-600" />
                </div>
                <p className="text-sm font-semibold text-slate-900">Pay</p>
                <p className="text-xs text-slate-500">Send from Vault</p>
              </button>
            </div>

            {/* Yield ledger */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => setShowLedger((s) => !s)}
                className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition"
                data-testid="vault-ledger-toggle"
              >
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-[#007377]" />
                  <div className="text-left">
                    <p className="font-bold text-slate-900">Yield ledger</p>
                    <p className="text-xs text-slate-500">Daily accrual log · last 30 days</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition ${showLedger ? "rotate-180" : ""}`} />
              </button>
              {showLedger && (
                <div className="border-t border-slate-100 max-h-96 overflow-y-auto" data-testid="vault-ledger-list">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-xs text-slate-500 uppercase tracking-wide">
                        <th className="text-left px-5 py-2 font-semibold">Date</th>
                        <th className="text-right px-5 py-2 font-semibold">Yield</th>
                        <th className="text-right px-5 py-2 font-semibold">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.slice().reverse().map((d, i) => (
                        <tr key={d.date} className="border-t border-slate-100" data-testid={`vault-ledger-row-${i}`}>
                          <td className="px-5 py-2.5 text-slate-700">{d.date}</td>
                          <td className="px-5 py-2.5 text-right text-emerald-600 font-semibold">
                            +${d.accrual.toFixed(4)}
                          </td>
                          <td className="px-5 py-2.5 text-right text-slate-900 font-semibold">
                            ${d.balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-400 text-center mt-5">
              Vault is a high-yield savings product. Funds remain at {brand.name}.
              Rates current as of today and may change.
            </p>
          </>
        )}
      </main>

      {modal === "deposit" && (
        <AmountModal
          title="Deposit to Vault"
          subtitle="Move funds from Checking to your insured Vault savings"
          submitLabel="Deposit"
          max={walletBalance}
          testIdPrefix="vault-deposit"
          onClose={() => setModal(null)}
          onSubmit={async (amt) => {
            await axios.post(`${API}/v2/vault/deposit`, { amount: amt });
            toast(`Deposited $${amt.toFixed(2)} to Vault`);
            await refresh();
          }}
        />
      )}
      {modal === "withdraw" && (
        <AmountModal
          title="Withdraw from Vault"
          subtitle="Move funds from Vault to your Checking"
          submitLabel="Withdraw"
          max={realPrincipal}
          testIdPrefix="vault-withdraw"
          onClose={() => setModal(null)}
          onSubmit={async (amt) => {
            await axios.post(`${API}/v2/vault/withdraw`, { amount: amt });
            toast(`Withdrew $${amt.toFixed(2)} from Vault`);
            await refresh();
          }}
        />
      )}
      {modal === "pay" && (
        <VaultPayModal
          max={realPrincipal}
          onClose={() => setModal(null)}
          onDone={async (msg) => { toast(msg); await refresh(); setModal(null); }}
        />
      )}

      {toastNode}
      <Footer />
    </div>
  );
};

const VaultPayModal = ({ max, onClose, onDone }) => {
  const [phone, setPhone] = useState("+15559876543");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const amt = parseFloat(amount) || 0;

  const submit = async () => {
    if (!phone) { setErr("Enter recipient phone"); return; }
    if (amt <= 0) { setErr("Enter an amount"); return; }
    if (amt > max) { setErr(`Max: $${max.toFixed(2)}`); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/v2/vault/pay`, {
        recipient_phone: phone, amount: amt, memo,
      });
      onDone(`Paid $${amt.toFixed(2)} from Vault · rail: ${res.data.rail}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="vault-pay-modal">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Pay from Vault</h3>
          <p className="text-xs text-slate-500 mt-1">Routes via Tokenized Deposit rail · instant settlement</p>
        </div>

        <div>
          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Recipient phone</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono"
            data-testid="vault-pay-phone" />
        </div>

        <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200 px-4 py-3">
          <span className="text-3xl font-bold text-slate-400">$</span>
          <input type="number" autoFocus value={amount} placeholder="0.00"
            onChange={(e) => { setAmount(e.target.value); setErr(""); }}
            className="flex-1 bg-transparent outline-none text-3xl font-bold"
            data-testid="vault-pay-amount" />
        </div>
        <p className="text-xs text-slate-500">Available in Vault: ${max.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>

        <input type="text" value={memo} onChange={(e) => setMemo(e.target.value)}
          placeholder="Memo (optional)"
          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          data-testid="vault-pay-memo" />

        {err && <p className="text-xs text-rose-600">{err}</p>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="vault-pay-submit"
            className="flex-1 py-3 rounded-xl bg-[#007377] text-white font-bold hover:bg-[#005a5d] disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Pay"}
          </button>
        </div>
      </div>
    </div>
  );
};
